import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { listenOutsideAngular } from '@iupics-util/tools/zone-listener.util';
import SmartButtonUiComponent from '@iupics-components/standard/fields/smart-button-ui/smart-button-ui.component';
import { SmartButtonManagerService } from '@iupics-manager/managers/smart-button-manager/smart-button-manager.service';
import { TranslateModule } from '@ngx-translate/core';

/** width kept for the "+N" button when some links do not fit */
const MORE_WIDTH = 64;

@Component({
  selector: 'iu-menu-smart-buttons-ui',
  templateUrl: './menu-smartbuttons-ui.component.html',
  styleUrls: ['./menu-smartbuttons-ui.component.scss'],
  standalone: true,
  imports: [SmartButtonUiComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class MenuSmartbuttonsUiComponent implements AfterViewInit, OnDestroy {
  readonly #ref: ElementRef<HTMLElement> = inject(ElementRef);
  readonly #smManager = inject(SmartButtonManagerService);
  readonly #zone = inject(NgZone);

  tabId = input.required<number>();
  windowId = input.required<number>();

  @ViewChild('row', { static: true }) row: ElementRef<HTMLUListElement>;
  @ViewChild('moreBtn') moreBtn: ElementRef<HTMLButtonElement>;

  get registration() {
    return this.#smManager.registration;
  }

  /** links of the record (not the one of this window, not the opened ones) */
  readonly buttons = computed(
    () => this.registration.data()?.filter((b) => !b.isOpened && b.key !== this.windowId()) ?? []
  );
  /** keys of the links that do not fit on the line */
  readonly #hiddenKeys = signal<Set<number>>(new Set());
  readonly hidden = computed(() => this.buttons().filter((b) => this.#hiddenKeys().has(b.key)));
  readonly open = signal(false);

  #observer: ResizeObserver | undefined;
  #frame = 0;

  constructor() {
    // new links: measured again once drawn
    effect(() => {
      this.buttons();
      this.#schedule();
    });
  }

  ngAfterViewInit() {
    if (typeof ResizeObserver !== 'undefined') {
      this.#observer = new ResizeObserver(() => this.#schedule());
      this.#observer.observe(this.#ref.nativeElement);
    }
    this.#schedule();
  }

  ngOnDestroy() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #schedule() {
    this.#zone.runOutsideAngular(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#fit());
    });
  }

  /** shows the links in order while they fit; the class is set here only (measured without it) */
  #fit() {
    const host = this.#ref.nativeElement;
    const row = this.row?.nativeElement;
    // hidden (record behind another blade): keep the last result
    if (!row || host.clientWidth === 0) return;
    const items = Array.from(row.querySelectorAll<HTMLElement>(':scope > li'));
    items.forEach((li) => li.classList.remove('smart-button--overflow'));
    const widths = items.map((li) => li.getBoundingClientRect().width);
    const room = host.clientWidth;
    const hidden = new Set<number>();
    if (widths.reduce((a, b) => a + b, 0) > room) {
      let used = 0;
      let full = false;
      items.forEach((li, i) => {
        if (!full && used + widths[i] <= room - MORE_WIDTH) used += widths[i];
        else {
          full = true;
          hidden.add(Number(li.dataset['key']));
          li.classList.add('smart-button--overflow');
        }
      });
    }
    const before = this.#hiddenKeys();
    if (hidden.size !== before.size || [...hidden].some((k) => !before.has(k))) {
      this.#zone.run(() => {
        this.#hiddenKeys.set(hidden);
        if (!hidden.size) this.open.set(false);
      });
    }
  }

  toggle(event: Event) {
    event.stopPropagation();
    this.open.update((o) => !o);
  }

  close(focusButton = false) {
    if (!this.open()) return;
    this.open.set(false);
    if (focusButton) setTimeout(() => this.moreBtn?.nativeElement.focus());
  }

  // a click elsewhere closes the "+N" menu (outside Angular: no app check on every click)
  readonly #documentClick = listenOutsideAngular(
    'click',
    () => !!this.open(),
    () => this.close()
  );
}

export type SmartButtonMetaData = {
  tabId: number;
  recordId: string;
  parent: any;
};
